import { useState, FormEvent } from 'react';
import { useScrollReveal, animStyle } from '../../../hooks/useScrollReveal';
import PolicyModal from './PolicyModal';

const FORM_URL = 'https://readdy.ai/api/form/d75ic04prit363gl16j0';

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [modalSitePolicy, setModalSitePolicy] = useState(false);
  const [modalPrivacy, setModalPrivacy] = useState(false);
  const [modalSecurity, setModalSecurity] = useState(false);
  const [nameKanaError, setNameKanaError] = useState('');
  const [emailConfirmError, setEmailConfirmError] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [emailConfirmValue, setEmailConfirmValue] = useState('');

  const { ref: headerRef, visible: headerVisible } = useScrollReveal(0.3);
  const { ref: formRef, visible: formVisible } = useScrollReveal(0.1);
  const { ref: infoRef, visible: infoVisible } = useScrollReveal(0.1);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // ひらがなバリデーション
    const nameKanaInput = form.querySelector<HTMLInputElement>('input[name="name_kana"]');
    const hiraganaRegex = /^[\u3040-\u309F\s　]+$/;
    if (nameKanaInput && !hiraganaRegex.test(nameKanaInput.value)) {
      setNameKanaError('ひらがなのみ入力してください。');
      return;
    }
    setNameKanaError('');

    // メールアドレス一致バリデーション
    if (emailValue !== emailConfirmValue) {
      setEmailConfirmError('メールアドレスが一致しません');
      return;
    }
    setEmailConfirmError('');

    const textarea = form.querySelector('textarea');
    if (textarea && textarea.value.length > 500) {
      setError('お問い合わせ内容は500文字以内でご入力ください。');
      return;
    }
    setSubmitting(true);
    setError('');
    const data = new URLSearchParams();
    const fields = form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select');
    fields.forEach((field) => {
      if (field.name && field.name !== 'email_confirm') data.append(field.name, field.value);
    });
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
        form.reset();
        setCharCount(0);
        setEmailValue('');
        setEmailConfirmValue('');
      } else {
        setError('送信に失敗しました。しばらくしてからお試しください。');
      }
    } catch {
      setError('送信に失敗しました。しばらくしてからお試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative w-full bg-[#1a1a1a]">
      <div className="w-full px-5 sm:px-8 md:px-20 py-14 md:py-20">

        {/* Section header */}
        <div ref={headerRef} className="mb-10 md:mb-14">
          <div
            style={{
              width: headerVisible ? '32px' : '0px',
              height: '2px',
              backgroundColor: '#fff',
              marginBottom: '24px',
              transition: 'width 0.7s ease-out 0ms',
            }}
          />
          <h2
            className="text-5xl md:text-6xl font-bold text-white tracking-widest font-['Montserrat']"
            style={animStyle(headerVisible, 'fromLeft', 150, 750)}
          >
            CONTACT
          </h2>
        </div>

        {/* Main grid: Form (left) | Info + Map (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-16 max-w-6xl">

          {/* ── Form ── */}
          <div ref={formRef} style={animStyle(formVisible, 'fromLeft', 80, 800)}>
            {submitted ? (
              <div className="text-white/80 text-sm leading-relaxed py-8">
                <div className="w-12 h-12 flex items-center justify-center border border-white/30 rounded-full mb-4">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <p className="text-lg text-white mb-2">送信が完了しました。</p>
                <p className="text-white/60">お問い合わせありがとうございました。</p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 text-sm text-white/50 hover:text-white underline cursor-pointer whitespace-nowrap"
                >
                  別のお問い合わせをする
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
                {/* 御社名・担当者名 — stacked on mobile */}
                <div>
                  <label className="block text-white/70 text-xs mb-1 tracking-wider">
                    御社名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    maxLength={100}
                    placeholder="例：株式会社〇〇"
                    className="w-full bg-white/5 border border-white/20 text-white text-sm px-4 py-3 focus:outline-none focus:border-white/50 transition-colors placeholder:text-white/25"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-xs mb-1 tracking-wider">
                    ご担当者名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="例：山田 太郎"
                    className="w-full bg-white/5 border border-white/20 text-white text-sm px-4 py-3 focus:outline-none focus:border-white/50 transition-colors placeholder:text-white/25"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-xs mb-1 tracking-wider">
                    ご担当者名（かな） <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name_kana"
                    required
                    placeholder="例：やまだ たろう"
                    onChange={() => setNameKanaError('')}
                    className={`w-full bg-white/5 border text-white text-sm px-4 py-3 focus:outline-none transition-colors placeholder:text-white/25 ${nameKanaError ? 'border-red-400' : 'border-white/20 focus:border-white/50'}`}
                  />
                  {nameKanaError && (
                    <p className="text-red-400 text-xs mt-1">{nameKanaError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-white/70 text-xs mb-1 tracking-wider">
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    maxLength={250}
                    placeholder="例：info@company.co.jp"
                    value={emailValue}
                    onChange={(e) => {
                      setEmailValue(e.target.value);
                      if (emailConfirmValue && e.target.value !== emailConfirmValue) {
                        setEmailConfirmError('メールアドレスが一致しません');
                      } else {
                        setEmailConfirmError('');
                      }
                    }}
                    className="w-full bg-white/5 border border-white/20 text-white text-sm px-4 py-3 focus:outline-none focus:border-white/50 transition-colors placeholder:text-white/25"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-xs mb-1 tracking-wider">
                    Email（確認用） <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email_confirm"
                    required
                    maxLength={250}
                    placeholder="もう一度メールアドレスを入力してください"
                    value={emailConfirmValue}
                    onChange={(e) => {
                      setEmailConfirmValue(e.target.value);
                      if (emailValue && e.target.value !== emailValue) {
                        setEmailConfirmError('メールアドレスが一致しません');
                      } else {
                        setEmailConfirmError('');
                      }
                    }}
                    className={`w-full bg-white/5 border text-white text-sm px-4 py-3 focus:outline-none transition-colors placeholder:text-white/25 ${emailConfirmError ? 'border-red-400' : 'border-white/20 focus:border-white/50'}`}
                  />
                  {emailConfirmError && (
                    <p className="text-red-400 text-xs mt-1">{emailConfirmError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-white/70 text-xs mb-1 tracking-wider">
                    件名 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    placeholder="例：サービス導入についてのご相談"
                    className="w-full bg-white/5 border border-white/20 text-white text-sm px-4 py-3 focus:outline-none focus:border-white/50 transition-colors placeholder:text-white/25"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-xs mb-1 tracking-wider">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    maxLength={50}
                    placeholder="例：03-0000-0000"
                    className="w-full bg-white/5 border border-white/20 text-white text-sm px-4 py-3 focus:outline-none focus:border-white/50 transition-colors placeholder:text-white/25"
                  />
                </div>

                <div>
                  <label className="block text-white/70 text-xs mb-1 tracking-wider">
                    お問い合わせの種類 <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="inquiry_type"
                      required
                      defaultValue=""
                      className="w-full appearance-none bg-white/5 border border-white/20 text-white text-sm px-4 py-3 focus:outline-none focus:border-white/50 transition-colors cursor-pointer pr-10"
                    >
                      <option value="" disabled className="bg-[#1a1a1a] text-white/40">選択してください</option>
                      <option value="サービスについて" className="bg-[#1a1a1a] text-white">サービスについて</option>
                      <option value="採用について" className="bg-[#1a1a1a] text-white">採用について</option>
                      <option value="その他" className="bg-[#1a1a1a] text-white">その他</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-10 h-full flex items-center justify-center">
                      <i className="ri-arrow-down-s-line text-white/40 text-base" />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-xs mb-1 tracking-wider">
                    お問い合わせ内容 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="お問い合わせの詳細をご入力ください。&#10;（例：〇〇のサービスについて詳しく話を聞きたい、資料請求をしたい、など）"
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-white/5 border border-white/20 text-white text-sm px-4 py-3 focus:outline-none focus:border-white/50 transition-colors resize-none placeholder:text-white/25"
                  />
                  <p className={`text-xs mt-1 text-right ${charCount > 500 ? 'text-red-400' : 'text-white/40'}`}>
                    {charCount}/500文字
                  </p>
                </div>

                <div className="text-white/40 text-xs leading-relaxed">
                  ※以下にご案内する当社の「個人情報保護方針」「情報セキュリティ方針」に同意の上、【送信する】ボタンを選択してください。
                  <br />
                  <button
                    type="button"
                    onClick={() => setModalSitePolicy(true)}
                    className="underline hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    サイトポリシー
                  </button>
                  <br />
                  <button
                    type="button"
                    onClick={() => setModalPrivacy(true)}
                    className="underline hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    個人情報保護方針
                  </button>
                  <br />
                  <button
                    type="button"
                    onClick={() => setModalSecurity(true)}
                    className="underline hover:text-white/70 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    情報セキュリティ方針
                  </button>
                </div>

                {error && <p className="text-red-400 text-xs">{error}</p>}

                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="w-full sm:w-auto bg-white text-black text-sm font-medium tracking-widest px-10 py-3.5 hover:bg-white/80 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {submitting ? '送信中...' : '送信する'}
                </button>
              </form>
            )}
          </div>

          {/* ── Info + Map ── */}
          <div className="flex flex-col gap-8">

            {/* Contact info cards */}
            <div ref={infoRef} className="space-y-4" style={animStyle(infoVisible, 'fromRight', 150, 800)}>
              <h3 className="text-white text-sm tracking-widest mb-6">
                お問い合わせはこちらからお願いします。
              </h3>

              {/* Address */}
              <div
                className="flex items-start gap-4 border-l border-white/15 pl-4 py-1"
                style={animStyle(infoVisible, 'fromRight', 220, 700)}
              >
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className="ri-map-pin-line text-white/50 text-base" />
                </div>
                <div>
                  <p className="text-white/50 text-xs tracking-wider mb-1">ADDRESS</p>
                  <p className="text-white/90 text-sm leading-relaxed">株式会社 Groove Gravity</p>
                  <p className="text-white/60 text-xs leading-relaxed mt-0.5">
                    〒150-0002<br />
                    東京都渋谷区渋谷3-6-2<br />
                    エクラート渋谷5F
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div
                className="flex items-start gap-4 border-l border-white/15 pl-4 py-1"
                style={animStyle(infoVisible, 'fromRight', 310, 700)}
              >
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className="ri-phone-line text-white/50 text-base" />
                </div>
                <div>
                  <p className="text-white/50 text-xs tracking-wider mb-1">PHONE / FAX</p>
                  <p className="text-white/90 text-sm">TEL：03-5324-2299</p>
                  <p className="text-white/60 text-xs mt-0.5">FAX：03-6869-9467</p>
                </div>
              </div>

              {/* Access */}
              <div
                className="flex items-start gap-4 border-l border-white/15 pl-4 py-1"
                style={animStyle(infoVisible, 'fromRight', 390, 700)}
              >
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className="ri-train-line text-white/50 text-base" />
                </div>
                <div>
                  <p className="text-white/50 text-xs tracking-wider mb-1">ACCESS</p>
                  <p className="text-white/90 text-sm">JR・各線「渋谷駅」徒歩約5分</p>
                  <p className="text-white/60 text-xs mt-0.5">渋谷スクランブルスクエア近く</p>
                </div>
              </div>

              {/* Business hours */}
              <div
                className="flex items-start gap-4 border-l border-white/15 pl-4 py-1"
                style={animStyle(infoVisible, 'fromRight', 460, 700)}
              >
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className="ri-time-line text-white/50 text-base" />
                </div>
                <div>
                  <p className="text-white/50 text-xs tracking-wider mb-1">BUSINESS HOURS</p>
                  <p className="text-white/90 text-sm">平日 10:00 〜 18:00</p>
                  <p className="text-white/60 text-xs mt-0.5">土日祝・年末年始を除く</p>
                </div>
              </div>
            </div>



          </div>
        </div>
      </div>

      {/* ── サイトポリシー Modal ── */}
      <PolicyModal isOpen={modalSitePolicy} onClose={() => setModalSitePolicy(false)} title="サイトポリシー">
        <p>以下の内容は、株式会社Groove Gravity（以下「当社」といいます）が運営するこのウェブサイト（以下「本サイト」といいます）をご利用いただく際の利用条件等を定めたものです。</p>
        <p>本サイトを利用された場合は、以下の内容について同意したものとみなされます。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ 著作権</h4>
        <p>本サイトで公開されるテキスト、画像、イラスト、プログラム等すべての内容（以下「コンテンツ」といいます）に関する権利は、当社または原著作者に帰属します。<br />これらコンテンツの私的複製等は、当社または原著作者の許諾を得ることなく、法律によって認められる範囲を超えての使用（複製、改変、アップロード、定時、送信、頒布、ライセンス、販売、出版等を含む）はできません。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ 商標</h4>
        <p>本サイト内のすべての商標およびロゴマーク、商号に関する権利は、当社または個々の権利の所有者に帰属します。<br />商標権法そのほか法律により認められる場合を除き、正当な権限なく無断で使用することはできません。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ 免責事項</h4>
        <p>当社は、本サイトへのコンテンツの掲載には、細心の注意を払っておりますが、掲載されたコンテンツに含まれる情報の正確性・完全性について保証するものではありません。また、当社は理由のいかんにかかわらず、本サイト上のコンテンツを使用されたこと、あるいは使用できなかったこと、また本サイトを使用されたことによって生じたいかなる直接的損害、間接的損害等についても一切責任を負うことができません。<br />当社は予告なしに本サイト上のコンテンツを変更することがあります。また本サイトの運営を中断ないしは中止する場合があります。これらによって生じたいかなる直接的損害、間接的損害等についても一切責任を負うことができません。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ 禁止事項</h4>
        <p>本サイトのご利用に際し、以下の行為を禁止します。</p>
        <p>・本サイトの運営を妨げる行為、および支障をきたす行為<br />・他の利用者、第三者もしくは当社に迷惑、不利益、損害を与える行為、またはその恐れがある行為<br />・公序良俗に反する行為、またはその恐れがある行為<br />・法律、法令もしくは条例に違反する行為、またはその恐れがある行為<br />・その他、当社が不適切と判断する行為</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ リンクサイト</h4>
        <p>本サイトからのリンクに関し、リンク先のウェブサイトは、それぞれの運営者の責任によって管理されるものであり、その内容に関してなんら保証はできません。また、それらを利用したことによって生じたいかなる直接的損害、間接的損害等についても一切責任を負うことができません。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ 本サイトへのリンク</h4>
        <p>本サイトへのリンクは、営利、非営利に関わらず原則自由とします。ただし、以下のいずれかに該当するか、またはその恐れがあるリンクの設定はできません。</p>
        <p>・当社または他者・他団体を誹謗中傷するか、信用失墜をいいとする内容を含んだウェブサイト<br />・当社または他者・他団体の著作権、商標権などの知的財産権、財産、プライバシーもしくは肖像権その他の権利を侵害する行為、または侵害する恐れのあるウェブサイト<br />・フレーム内で本サイトが展開されるなど、当社のコンテンツであることが不明確となり、第三者に誤解を与える可能性があるウェブサイト<br />・上記各項目の他、法律、条令、法令または公序良俗に違反する行為、本サイトの運営を妨害する恐れのあるウェブサイト</p>
        <p>当社が保有するロゴ等の登録商標を無断で用いてリンクを張ることは認めておりません。なお、本サイトのURLは予告なく変更する場合があります。<br />また、リンクを張ったことによって生じたいかなる直接的損害、間接的損害等についても一切責任を負うことができません。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ 推奨環境</h4>
        <p>本サイトでは、より安全で快適にご利用いただくために下記のブラウザを推奨しています。</p>
        <p>Windowsをご利用の場合<br />　Microsoft Edge、Mozilla Firefox、Google Chrome 各最新バージョン</p>
        <p>Macをご利用の場合<br />　Microsoft Edge、Mozilla Firefox、Google Chrome 各最新バージョン</p>
        <p>スマートフォン・タブレット / iOSの場合<br />　Mobile Safari、Google Chrome 各最新バージョン</p>
        <p>スマートフォン・タブレット / Androidの場合<br />　Mobile Safari、Google Chrome 各最新バージョン</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ cookieおよびwebビーコン</h4>
        <p>本サイトでは、cookieおよびwebビーコンを使用していますが、これらは特定の個人を識別する機能を持っていません。また、当社は、特定個人を識別するために、これらの情報を別の情報と照合することもいたしません。利用の目的は、本サイトの訪問履歴等を分析し、当社サービスのマーケティング及びWebサイトの改善等を行うことです。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ JavaScript</h4>
        <p>本サイトでは、一部のコンテンツにおいてJavaScriptを使用しています。<br />ご使用のブラウザ設定においてJavaScriptを有効にされていない場合には、正しく表示されないまたは操作できない場合があります。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ SSL</h4>
        <p>本サイトでは、セキュリティレベルを向上するために、一部のサービスにおいてSSL暗号化通信を用いています。<br />該当するサービスをご利用いただく場合には、ご利用のブラウザが128ビットSSLに対応していることが必要となります。また、ファイアウォール等の設定により、該当するサービスをご利用いただけない場合があります。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ 準拠法および管轄裁判所</h4>
        <p>本サイトのご利用ならびにサイトポリシーの解釈および適用は、他に別段の定めがない限り日本国法に準拠するものとします。本サイトに関する全ての紛争については、他に別段の定めがない限り東京地方裁判所を第一審の専属管轄裁判所とします。</p>

        <p className="mt-6 text-white/40">2020年７月１日 制定<br />2022年3月25日 改定<br />株式会社Groove Gravity<br />代表取締役　上梨能寛</p>
      </PolicyModal>

      {/* ── 個人情報保護方針 Modal ── */}
      <PolicyModal isOpen={modalPrivacy} onClose={() => setModalPrivacy(false)} title="個人情報保護方針">

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-2">１．個人情報に対する基本方針</h4>
        <p>株式会社Groove Gravity（以下「当社」といいます）は、個人情報保護を企業における重要な社会的使命・責務と認識し、当社が保有する個人情報を適切に管理運用するために遵守すべき基本事項として本保護方針を定めます。</p>
        <p>・当社は、業務の遂行上必要がない限り、個人情報の取得、利用および保有をいたしません。<br />・当社は、保有する個人情報が業務の遂行に不必要となった場合は、適切な方法により、速やかな消去または廃棄に努めます。<br />・当社は、個人情報の取得、利用および保有の必要が生じた場合は、次項以下に基づき個人情報の適切な取り扱いに努めます。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">２．安全管理措置</h4>
        <p>当社は、基本方針に則り、次のとおり安全管理措置を講じております。</p>
        <p>・個人情報の取扱いに関する規程の整備と、従業員への周知徹底<br />・個人情報取扱いに関する体制の整備・改善と定期的監査の実施<br />・従業員に対する教育の実施、機密保持に関する誓約書の締結<br />・個人情報を取扱うエリアへの入退館管理及び個人情報へのアクセス制御<br />・個人情報へのアクセス制御、ウイルス対応等の技術的措置<br />・外部の事業者に個人データを委託する場合の情報セキュリティ評価・監督</p>
        <p>当社は、個人情報の安全管理措置として、クラウドサービスを利用しております。当社が利用するクラウドサービスは、ISO 27001、ISO 27018、SSAE18 SOC1、SOC2、HIPAA、EUモデル条項(EUMC)の標準を使用しています。個人情報及び個人関連情報は、原則として日本国内に保管されます。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">３．個人情報の定義</h4>
        <p>当社において、個人情報とは、個人の識別に係る以下の情報をいいます。</p>
        <p>住所・氏名・電話番号・電子メールアドレス、ニックネーム、IPアドレス等において、特定の個人を識別できる情報であり、他の情報と照合することで、特定の個人を識別できることができるものを含みます。<br />当社の運営・提供するウェブサイト、ソーシャルメディアにおける、ご利用者様のご利用内容、ご利用履歴等の情報を含みます。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">４．個人情報の取得・収集</h4>
        <p>当社は、個人情報の保護に関する法令の規定および行政機関その他が特に定めた規範およびガイドライン等を遵守し、以下の方法により、適法かつ公正な手段により、個人情報を取得します。</p>
        <p>・当社のウェブサイト、ソーシャルメディア等を通じて取得・収集させていただく方法<br />・電子メール、郵便、書面、電話等の手段により、取得・収集させていただく方法<br />・当社と面会等された際に情報を取得させていただく方法<br />・当社の受託業務に伴い、委託元から情報を取得する方法</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">５．個人情報の利用目的</h4>
        <p>当社は以下の目的のため、個人情報を適法かつ公正な手段で利用させていただきます。当社は、ご本人の同意がある場合を除き、以下の目的達成に必要な範囲を超えて、取得した個人情報を利用しません。</p>
        <p>・当社ソーシャルマーケティング・サービス業務に関わる個人情報であって、電子メールアドレス、ニックネーム、ご利用者様のご利用内容、ご利用履歴（個人関連情報）を含み、個人の趣味・嗜好等に応じた情報提供をするために利用します。受託業務に伴い委託を受けたサービスを含みます。<br />・当社ソーシャルマーケティング・サービス業務に関わる個人情報であって、住所、氏名、電話番号、電子メールアドレス、ニックネームを含み、個人へ物品或いはデータ、メッセージを送付するために利用します。受託業務に伴い委託を受けたサービスを含みます。<br />・当社ソーシャルマーケティング・サービス業務に関わる個人情報であって、ニックネーム、ご利用者様のご利用内容、ご利用履歴を含み、顧客へのご提案作成、情報提供、商談、連絡、契約履行のために利用します。<br />・当社事業のソリューション力向上のための個人情報であって、電子メールアドレス、ニックネーム、ご利用者様のご利用内容、ご利用履歴を含み、調査・データ集積、改善、研究開発のために利用します。<br />・当社ソーシャルマーケティング・サービス、コンサルティング、開発業務に関わる個人情報であって、住所、氏名、電話番号、電子メールを含み、顧客との連絡業務、顧客先管理業務、契約業務のために利用します。<br />・当社サービスに関する利用者への対応業務のための個人情報であって、住所・氏名・電話番号・電子メールアドレス、ニックネーム、IPアドレスを含み、セミナー・サービスのご案内、お問合せ対応のために利用します。<br />・当社のサイトポリシー等への違反対応業務に関わる個人情報であって、住所・氏名・電話番号・電子メールアドレス、ニックネーム、IPアドレスを含み、通知・連絡、訴訟のために利用します。<br />・当社従業員及び退職者の管理業務のための個人情報であって、住所・氏名・電話番号・電子メールアドレス、ニックネーム、マイナンバーを含み、人事・労務管理、法令に定められる健康診断、福利厚生、緊急連絡業務のために利用します。<br />・当社従業員の採用活動業務に関わる個人情報であって、住所、氏名、電話番号、写真、電子メールアドレス、ニックネームを含み、通知・連絡業務、雇用候補者管理業務のために利用します。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">６．個人情報の取扱いの委託</h4>
        <p>当社では、業務の全部または一部を委託し、当該委託先に対して、利用目的の達成に必要な範囲内で個人情報の取扱いを委託する場合があります。この場合には、当社の定めた基準を満たす者を委託先として選定するとともに、安全管理が図られるように個人情報の取扱いに関する契約の締結や適切な管理・監督を行います。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">７．個人情報の第三者への提供・開示</h4>
        <p>当社は、前項および以下のいずれかに該当する場合を除き、ご本人の同意を得ることなく、取得した個人情報を、第三者に提供または開示いたしません。</p>
        <p>・法令に基づく場合<br />・公的機関等またはそれらからの委託を受けた者より、法令の定める内容の開示請求をうけたとき<br />・人の生命、身体や財産の保護のために必要な場合であって、ご本人の同意を得ることが困難なとき<br />・合併、会社分割または事業譲渡等その他の事由により事業が承継されるとき</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">８．個人情報の開示・訂正・利用停止</h4>
        <p>当社は、ご本人から個人情報の開示、内容の訂正・追加・削除および利用停止を求められた場合には適切にこれに対応します。ただし、個人情報保護法その他の法令により、当社がこれらの義務を負わない場合は、この限りではありません。<br />開示に当たっては、ご本人が指定する方法により対応させていただきますが、方法によっては費用が発生する場合があります。手続きの詳細に関しては、当社お問い合せフォームよりご連絡ください。<br />ご意見、ご質問、苦情のお申し出その他についても、同様に当社お問い合せフォームよりご連絡ください。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">９．継続的改善</h4>
        <p>当社は、個人情報保護に関する取扱い、管理および管理体制について継続的改善を行います。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">10．改定</h4>
        <p>本保護方針は、当社の判断によりご本人の同意なしに全部または一部の改訂を行うことができるものとします。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">11．問い合わせ窓口</h4>
        <p>当社は、ご本人からの個人情報の取扱いに関するお問い合わせに対し、適切に対応いたします。当社の問い合わせ窓口は、下記のとおりです。</p>
        <p>東京都渋谷区渋谷3－6－2　エクラート渋谷5F<br /><strong className="text-white/80">株式会社Groove Gravity</strong><br />個人情報の取扱いに関するお問い合わせ窓口係</p>
        <p>お問合せ方法　当社お問い合せフォームよりご連絡ください</p>

        <p className="mt-6 text-white/40">2020年７月１日 制定<br />2022年3月25日 改定<br />株式会社Groove Gravity<br />代表取締役　上梨能寛</p>
      </PolicyModal>

      {/* ── 情報セキュリティ方針 Modal ── */}
      <PolicyModal isOpen={modalSecurity} onClose={() => setModalSecurity(false)} title="情報セキュリティ方針">

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-2">■ 基本理念</h4>
        <p>株式会社Groove Gravity（以下、当社）は、人と人がつながるサービスが不可欠な時代に、みなさまの事業へGrooveを生み出し、当社のチカラで牽引してゆきたいという理念をもって、事業を行っています。</p>
        <p>当社の事業の中で取り扱う、お客様の情報をはじめとする情報資産は、当社の経営基盤として極めて重要なものです。</p>
        <p>漏洩、き損、滅失等のリスクから、情報資産を保護することの重要性を認識した役員や従業員を含めた、情報資産を扱う者が本方針を遵守し、情報資産の機密性、完全性、可用性といった情報セキュリティを維持するための活動を実践します。</p>

        <h4 className="text-white/90 text-xs font-semibold tracking-wider mt-4">■ 基本方針</h4>
        <p>情報資産を保護するために、情報セキュリティ方針ならびに、それにまつわる規程類及び安全管理措置を策定し、これに従って業務を行うとともに、情報セキュリティに関連する法令、規制その他の規範、及び、お客様との契約事項を遵守いたします。</p>

        <p>情報資産に対して存在する漏洩、き損、滅失等のリスクを分析、評価するための基準を明確にし、体系的なリスクアセスメント方法を確立するとともに、定期的にリスクアセスメントを実施いたします。また、その結果に基づき、必要かつ適切なセキュリティ対策を実施いたします。</p>

        <p>担当役員を中心とした情報セキュリティ体制を確立するとともに、情報セキュリティに関する権限および責任を明確にいたします。また、すべての従業者が、情報セキュリティの重要性を認識し、情報資産の適切な取り扱いを確実にするために、定期的に教育、訓練および啓発を行います。</p>

        <p>情報セキュリティポリシーの遵守状況及び情報資産の取扱いについて、定期的に点検及び監査を行い、発見された不備や改善項目については、速やかに是正処置を講じます。</p>

        <p>情報セキュリティ上のイベントやインシデントの発生に対する適切な処置を講じるとともに、万一それらが発生した場合に際して、あらかじめ、被害を最小限に留めるための対応手順を確立し、有事の際には、速やかに対応するとともに、適切な是正処置を講じます。また、特に、業務中断に関わるようなインシデントについては、その管理の枠組みを確立し、定期的に見直しを行うことにより、当社の事業継続を確実にいたします。</p>

        <p>基本理念を実現するための目標を定めた情報セキュリティマネジメントシステムを確立しこれを実行するとともに、第三者機関による情報セキュリティに関する認証を取得し、継続的に維持、改善を行います。</p>

        <p className="mt-6 text-white/40">2020年7月1日 制定<br />2022年3月25日 改定<br /><br />株式会社Groove Gravity<br />代表取締役　上梨能寛</p>
      </PolicyModal>
    </section>
  );
}
